/**
 * The ONE place Cbutton writes its own attributes onto a semantic root.
 *
 * All three roots (the div, the inline span and the `href` anchor) spread the
 * caller's `rootAttributes` and then need Cbutton's own attributes on top. Doing
 * that inline in the JSX put every one of them *after* the spread, which is a
 * silent-destruction seam: a caller passing `aria-disabled` through the bag had
 * it overwritten with `undefined`, and nothing — not the type checker, not the
 * call site, not the rendered DOM — said so. Four live call sites were losing an
 * attribute that way.
 *
 * The fix has two halves and both are needed:
 *
 * 1. Every attribute Cbutton owns is listed in `ControlledButtonRootAttribute`
 *    and therefore does not exist on `CbuttonRootAttributes` at all. A call site
 *    that spells one into the bag is a compile error naming the dedicated prop.
 * 2. This resolver is the only writer. Because half 1 guarantees the bag cannot
 *    carry a controlled attribute, writing them after the spread here is
 *    provably lossless — and a future attribute cannot be added "after the
 *    spread" by accident, because there is no after-the-spread position in the
 *    JSX any more. Adding one means adding it here AND to the controlled list.
 */
import type { AriaRole } from "react";
import type { CbuttonRootAttributes } from "./buttonTypes";

/**
 * The caller's passive attributes, plus the attributes Cbutton owns. The two
 * halves are disjoint by construction: everything below is omitted from
 * {@link CbuttonRootAttributes}.
 */
export type ResolvedButtonRootAttributes = CbuttonRootAttributes & {
  className?: string;
  title?: string;
  role?: AriaRole;
  tabIndex?: number;
  "aria-label"?: string;
  "aria-pressed"?: boolean;
  "aria-disabled"?: boolean;
};

export interface ResolveButtonRootAttributesInput {
  readonly rootAttributes?: CbuttonRootAttributes;
  readonly rootClassName?: string;
  readonly title?: string;
  readonly role?: AriaRole;
  readonly tabIndex?: number;
  readonly ariaLabel?: string;
  readonly ariaPressed?: boolean;
  readonly ariaDisabled?: boolean;
}

export function resolveButtonRootAttributes({
  rootAttributes,
  rootClassName,
  title,
  role,
  tabIndex,
  ariaLabel,
  ariaPressed,
  ariaDisabled,
}: ResolveButtonRootAttributesInput): ResolvedButtonRootAttributes {
  return {
    ...rootAttributes,
    className: rootClassName,
    title,
    role,
    tabIndex,
    "aria-label": ariaLabel,
    "aria-pressed": ariaPressed,
    "aria-disabled": ariaDisabled,
  };
}
